import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiArrowRight, 
  FiCheckCircle, 
  FiTarget, 
  FiTrendingUp, 
  FiMail, 
  FiLock, 
  FiUsers, 
  FiAward,
  FiZap,
  FiShield,
  FiClock,
  FiBarChart2,
  FiBriefcase,
  FiMessageSquare,
  FiFileText,
  FiStar
} from 'react-icons/fi';
import { api } from '../utils/api';
import { useRefreshListener, REFRESH_EVENTS } from '../utils/refreshEvents';
import './Home.css';

// Variants d'animation
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { y: 30, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: [0.4, 0, 0.2, 1]
    }
  }
};

const Home = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [homeImages, setHomeImages] = useState({
    heroImage: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800&h=1000&fit=crop',
    benefitsImage: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=1000&fit=crop'
  });

  const fetchServices = useCallback(async () => {
    try {
      console.log('🔍 Chargement des services...');
      console.log('📡 API_URL:', process.env.REACT_APP_API_URL || 'Non défini (utilise proxy)');
      const res = await api.getServices();
      console.log('✅ Réponse API:', res);
      console.log('📦 Données reçues:', res.data);
      if (res.data && Array.isArray(res.data)) {
        setServices(res.data);
        console.log(`✅ ${res.data.length} service(s) chargé(s)`);
      } else {
        console.warn('⚠️ Les données ne sont pas un tableau:', res.data);
        setServices([]);
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des services:', error);
      console.error('📋 Détails de l\'erreur:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHomeImages = useCallback(async () => {
    try {
      const res = await api.getHomeImages();
      if (res.data) {
        setHomeImages({
          heroImage: res.data.heroImage || 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800&h=1000&fit=crop',
          benefitsImage: res.data.benefitsImage || 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=1000&fit=crop'
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement des images:', error);
    }
  }, []);

  useEffect(() => {
    fetchServices();
    fetchHomeImages();
  }, [fetchServices, fetchHomeImages]);

  // Écouter les événements de rafraîchissement depuis l'admin
  useRefreshListener(REFRESH_EVENTS.SERVICES, fetchServices);
  useRefreshListener(REFRESH_EVENTS.HOME, fetchHomeImages);

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (email) {
      window.location.href = `/contact?email=${encodeURIComponent(email)}`;
    }
  };

  const benefits = [
    { 
      icon: FiClock, 
      text: 'Déléguer vos tâches administratives chronophages et gagner du temps précieux' 
    },
    { 
      icon: FiTarget, 
      text: 'Bénéficier d\'une stratégie claire et percutante pour votre entreprise' 
    },
    { 
      icon: FiMessageSquare, 
      text: 'Améliorer votre communication et votre image de marque' 
    },
    { 
      icon: FiBriefcase, 
      text: 'Recevoir un accompagnement personnalisé et professionnel' 
    },
    { 
      icon: FiBarChart2, 
      text: 'Transformer votre efficacité et accélérer votre croissance' 
    },
    { 
      icon: FiZap, 
      text: 'Optimiser vos processus et maximiser votre productivité' 
    }
  ];

  const stats = [
    { icon: FiUsers, number: '500+', label: 'Clients satisfaits', color: '#2d5016' },
    { icon: FiAward, number: '4', label: 'Pôles de services', color: '#4a7c2a' },
    { icon: FiTrendingUp, number: '11+', label: 'Formules disponibles', color: '#6b8e3f' },
    { icon: FiShield, number: '100%', label: 'Satisfaction garantie', color: '#a8c97f' }
  ];

  const serviceIcons = {
    strategie: FiTarget,
    communication: FiMessageSquare,
    administratif: FiFileText,
    soutien: FiBriefcase
  };

  return (
    <div className="home-ux">
      {/* Hero Section */}
      <section className="hero-ux">
        <div className="hero-background-ux">
          <div className="hero-gradient-ux"></div>
          <div className="hero-pattern-ux"></div>
        </div>
        
        <div className="container-ux">
          <div className="hero-content-ux">
            <motion.div
              className="hero-text-ux"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
            >
              {/* Badge */}
              <motion.div
                className="hero-badge-ux"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2, type: "spring" }}
              >
                <FiZap className="badge-icon" />
                <span>Communication & Stratégie</span>
              </motion.div>

              {/* Headline Principal */}
              <h1 className="hero-headline-ux">
                <span className="headline-line-1">Libérez votre temps,</span>
                <span className="headline-line-2">
                  <span className="highlight-wrapper">
                    <span className="highlight-text">gagnez en confiance</span>
                    <span className="highlight-underline"></span>
                  </span>
                </span>
                <span className="headline-line-3">réalisez vos projets</span>
              </h1>

              {/* Sous-titre */}
              <p className="hero-subtitle-ux">
                VirtualHand est votre partenaire pour déléguer efficacement, 
                optimiser votre organisation et transformer votre entreprise. 
                <strong> Pas juste une assistante, mais un véritable moteur de progression.</strong>
              </p>

              {/* CTA Principal */}
              <motion.form
                onSubmit={handleEmailSubmit}
                className="hero-cta-ux"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <div className="cta-input-group">
                  <div className="input-wrapper">
                    <FiMail className="input-icon-ux" />
                    <input
                      type="email"
                      placeholder="Votre adresse e-mail"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="cta-input-ux"
                    />
                  </div>
                  <motion.button
                    type="submit"
                    className="cta-button-primary"
                    whileHover={{ scale: 1.02, boxShadow: "0 12px 32px rgba(45, 80, 22, 0.4)" }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span>Commencer maintenant</span>
                    <FiArrowRight className="button-arrow" />
                  </motion.button>
                </div>
                <div className="cta-trust-ux">
                  <FiLock className="lock-icon-ux" />
                  <span>Vos données sont sécurisées. Aucun spam.</span>
                </div>
              </motion.form>

              {/* Social Proof */}
              <motion.div
                className="hero-social-proof"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <div className="social-proof-stars">
                  {[...Array(5)].map((_, i) => (
                    <FiStar key={i} className="star-icon" />
                  ))}
                </div>
                <p className="social-proof-text">
                  Rejoignez <strong>500+ entrepreneurs</strong> qui font confiance à VirtualHand
                </p>
              </motion.div>
            </motion.div>

            {/* Hero Visual */}
            <motion.div
              className="hero-visual-ux"
              initial={{ opacity: 0, x: 60, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 1, delay: 0.3, ease: [0.4, 0, 0.2, 1] }}
            >
              <div className="hero-image-wrapper-ux">
                <div className="hero-image-container">
                  <img
                    src={homeImages.heroImage}
                    alt="VirtualHand - Votre partenaire de confiance"
                    className="hero-image-ux"
                  />
                  <div className="hero-image-overlay"></div>
                </div>
                <div className="hero-image-glow-ux"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section Stats */}
      <section className="stats-section-ux">
        <div className="container-ux">
          <motion.div
            className="stats-grid-ux"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  className="stat-card-ux"
                  variants={itemVariants}
                  whileHover={{ 
                    y: -8, 
                    scale: 1.03,
                    boxShadow: "0 16px 48px rgba(0, 0, 0, 0.12)"
                  }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                  <div className="stat-icon-wrapper" style={{ '--stat-color': stat.color }}>
                    <Icon className="stat-icon-ux" />
                  </div>
                  <div className="stat-number-ux">{stat.number}</div>
                  <div className="stat-label-ux">{stat.label}</div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Section Services */}
      <section className="services-section-ux">
        <div className="container-ux">
          <motion.div
            className="section-header-ux"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="section-label">
              <FiAward className="label-icon" />
              Nos Services
            </span>
            <h2 className="section-title-ux">
              Les 4 pôles qui transforment
              <span className="title-accent"> votre entreprise</span>
            </h2>
            <p className="section-description">
              Des solutions complètes et personnalisées pour répondre à tous vos besoins
            </p>
          </motion.div>

          {loading ? (
            <div className="loading-ux">Chargement...</div>
          ) : services.length === 0 ? (
            <div className="no-services-ux">
              <p>Aucun service disponible pour le moment.</p>
              <p style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '0.5rem' }}>
                Vérifiez la console pour plus de détails.
              </p>
            </div>
          ) : (
            <motion.div
              className="services-grid-ux"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              {services.slice(0, 4).map((service, index) => {
                const ServiceIcon = serviceIcons[service.category] || FiBriefcase;
                return (
                  <motion.div
                    key={service._id}
                    className="service-card-ux"
                    variants={itemVariants}
                    whileHover={{ 
                      y: -12, 
                      scale: 1.02,
                      boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)"
                    }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  >
                    <div className="service-card-header">
                      <div className="service-icon-wrapper">
                        <ServiceIcon className="service-icon-large" />
                        <div className="service-icon-bg"></div>
                      </div>
                      <h3 className="service-title-ux">{service.title}</h3>
                    </div>
                    <p className="service-description-ux">{service.description}</p>
                    {service.features && service.features.length > 0 && (
                      <ul className="service-features-ux">
                        {service.features.slice(0, 3).map((feature, i) => (
                          <li key={i}>
                            <div className="feature-check-wrapper">
                              <FiCheckCircle className="feature-check" />
                            </div>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <Link to="/services" className="service-link-ux">
                      <span>En savoir plus</span>
                      <FiArrowRight className="link-arrow" />
                    </Link>
                  </motion.div>
                );
              })}
            </motion.div>
          )}

          <motion.div
            className="section-cta-ux"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Link to="/services" className="button-secondary">
              <span>Voir tous les services</span>
              <FiArrowRight />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Section Benefits */}
      <section className="benefits-section-ux">
        <div className="container-ux">
          <div className="benefits-layout-ux">
            <motion.div
              className="benefits-visual"
              initial={{ opacity: 0, x: -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
            >
              <div className="benefits-image-wrapper">
                <img
                  src={homeImages.benefitsImage}
                  alt="Bénéfices VirtualHand"
                  className="benefits-image-ux"
                />
                <div className="benefits-image-frame"></div>
              </div>
            </motion.div>

            <motion.div
              className="benefits-content"
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
            >
              <span className="section-label">
                <FiTarget className="label-icon" />
                Pourquoi VirtualHand
              </span>
              <h2 className="section-title-ux">
                Transformez votre façon
                <span className="title-accent"> de travailler</span>
              </h2>
              <ul className="benefits-list-ux">
                {benefits.map((benefit, index) => {
                  const Icon = benefit.icon;
                  return (
                    <motion.li
                      key={index}
                      className="benefit-item-ux"
                      initial={{ opacity: 0, x: 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      whileHover={{ x: 8 }}
                    >
                      <div className="benefit-icon-wrapper">
                        <Icon className="benefit-icon" />
                      </div>
                      <span>{benefit.text}</span>
                    </motion.li>
                  );
                })}
              </ul>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Link to="/contact" className="button-primary">
                  <span>Démarrer maintenant</span>
                  <FiArrowRight className="button-arrow" />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section CTA Final */}
      <section className="cta-final-section-ux">
        <div className="cta-final-bg">
          <div className="cta-gradient-overlay"></div>
        </div>
        <div className="container-ux">
          <motion.div
            className="cta-final-content"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
          >
            <div className="cta-badge-final">
              <FiZap className="badge-icon" />
              <span>Prêt à commencer ?</span>
            </div>
            <h2 className="cta-final-title">
              Transformez votre entreprise
              <span className="title-accent-white"> dès aujourd'hui</span>
            </h2>
            <p className="cta-final-description">
              Rejoignez des centaines d'entrepreneurs qui ont choisi VirtualHand 
              pour libérer leur temps et accélérer leur croissance.
            </p>
            <div className="cta-final-buttons">
              <Link to="/contact" className="button-primary button-large button-white">
                <span>Discuter de mon projet</span>
                <FiArrowRight className="button-arrow" />
              </Link>
              <Link to="/tarifs" className="button-secondary button-large button-white">
                <span>Voir les tarifs</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
